import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { callAuthedSupabaseFunction } from '../../lib/api'
import { supabase } from '../../lib/supabase'
import type { PublicXeroConnection } from '../../lib/xero-oauth'

function statusLabel(status: PublicXeroConnection['status'] | 'loading' | 'error') {
  if (status === 'connected') return 'Connected'
  if (status === 'accounts_required') return 'Connected — choose accounts'
  if (status === 'pending_tenant') return 'Connected — choose organisation'
  if (status === 'setup_required') return 'Not connected / not configured'
  return 'Not connected'
}

export function XeroConnectionPanel({
  onConnectionChange,
}: Readonly<{ onConnectionChange?: (connection: PublicXeroConnection | null) => void }>) {
  const [connection, setConnection] = useState<PublicXeroConnection | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [salesAccountCode, setSalesAccountCode] = useState('')
  const [depositAccountCode, setDepositAccountCode] = useState('')
  const [squareClearingAccountCode, setSquareClearingAccountCode] = useState('')

  const loadStatus = useCallback(async () => {
    setError(null)
    const { data } = await supabase.auth.getSession()
    const accessToken = data.session?.access_token
    if (!accessToken) {
      setError('Sign in again to manage the Xero connection.')
      return
    }
    const response = await callAuthedSupabaseFunction('xero-connection', accessToken)
    const payload = await response.json() as PublicXeroConnection & { ok?: boolean; error?: string }
    if (!payload.ok) {
      setError(payload.error || 'Unable to load the Xero connection.')
      return
    }
    setConnection(payload)
    setSalesAccountCode(payload.salesAccount?.code ?? '')
    setDepositAccountCode(payload.depositAccount?.code ?? '')
    setSquareClearingAccountCode(payload.squareClearingAccount?.code ?? '')
  }, [])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const xero = params.get('xero')
    if (xero === 'error') {
      setError(params.get('reason') || 'Xero authorisation did not complete.')
    } else if (xero === 'connected') {
      setMessage('Xero organisation authorised. Choose the sales and Stripe clearing accounts.')
    } else if (xero === 'select_org') {
      setMessage('Xero authorised more than one organisation. Choose which one Autoglow should use.')
    }
    void loadStatus()
  }, [loadStatus])

  useEffect(() => {
    onConnectionChange?.(connection)
  }, [connection, onConnectionChange])

  async function authedPost(name: string, body: Record<string, unknown>) {
    const { data } = await supabase.auth.getSession()
    const accessToken = data.session?.access_token
    if (!accessToken) throw new Error('Sign in again to manage the Xero connection.')
    const response = await callAuthedSupabaseFunction(name, accessToken, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    const payload = await response.json() as PublicXeroConnection & { ok?: boolean; error?: string; authorizeUrl?: string }
    if (!payload.ok) throw new Error(payload.error || 'Xero request failed.')
    return payload
  }

  async function connectXero() {
    setBusy(true)
    setError(null)
    try {
      const payload = await authedPost('xero-oauth-start', {})
      if (!payload.authorizeUrl) throw new Error('Xero did not return an authorisation URL.')
      window.location.assign(payload.authorizeUrl)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Xero authorisation could not start.')
      setBusy(false)
    }
  }

  async function selectTenant(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const tenantId = new FormData(event.currentTarget).get('tenantId')
    if (typeof tenantId !== 'string' || !tenantId) return
    setBusy(true)
    setError(null)
    try {
      const payload = await authedPost('xero-connection', { action: 'select_tenant', tenantId })
      setConnection(payload)
      setMessage('Organisation selected. Choose the sales and Stripe clearing accounts.')
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The Xero organisation was not selected.')
    } finally {
      setBusy(false)
    }
  }

  async function saveAccounts(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const payload = await authedPost('xero-connection', {
        action: 'save_accounts',
        salesAccountCode,
        depositAccountCode,
        squareClearingAccountCode,
      })
      setConnection(payload)
      setMessage('Xero sales and Stripe clearing accounts saved.')
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Xero accounts were not saved.')
    } finally {
      setBusy(false)
    }
  }

  async function disconnect() {
    setBusy(true)
    setError(null)
    try {
      const payload = await authedPost('xero-connection', { action: 'disconnect' })
      setConnection(payload)
      setSalesAccountCode('')
      setDepositAccountCode('')
      setSquareClearingAccountCode('')
      setMessage('Xero has been disconnected.')
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Xero could not be disconnected.')
    } finally {
      setBusy(false)
    }
  }

  const status = connection?.status ?? 'not_connected'

  return (
    <article className="admin-card">
      <div className="admin-card-heading">
        <div>
          <h2>Xero</h2>
          <p>{connection?.organisationName || 'No organisation connected'}</p>
        </div>
        <span className="status-pill">{statusLabel(status)}</span>
      </div>
      {message ? <p className="admin-alert">{message}</p> : null}
      {error ? <p className="admin-alert error">{error}</p> : null}
      {connection?.instructions && status === 'setup_required' ? (
        <p className="admin-empty-state">{connection.instructions}</p>
      ) : null}
      {connection?.organisationClassification === 'live' ? (
        <p className="admin-alert">
          This organisation is not a Xero Demo Company. Connecting is allowed, but invoice and
          payment writes stay blocked until live writes are explicitly approved.
        </p>
      ) : null}
      {status === 'pending_tenant' && (connection?.authorisedTenants?.length ?? 0) > 0 ? (
        <form className="admin-edit-grid" onSubmit={(event) => void selectTenant(event)}>
          <label>
            Xero organisation
            <select defaultValue="" name="tenantId" required>
              <option value="" disabled>Select organisation</option>
              {connection?.authorisedTenants?.map((tenant) => (
                <option key={tenant.tenantId} value={tenant.tenantId}>{tenant.tenantName}</option>
              ))}
            </select>
          </label>
          <button disabled={busy} type="submit">Use this organisation</button>
        </form>
      ) : null}
      {status === 'connected' || status === 'accounts_required' ? (
        <form className="admin-edit-grid" onSubmit={(event) => void saveAccounts(event)}>
          <label>
            Sales / revenue account
            <select onChange={(event) => setSalesAccountCode(event.target.value)} required value={salesAccountCode}>
              <option value="" disabled>Select sales account</option>
              {(connection?.salesAccounts ?? []).map((account) => (
                <option key={account.code} value={account.code}>
                  {account.code} — {account.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Stripe clearing BANK account
            <select onChange={(event) => setDepositAccountCode(event.target.value)} required value={depositAccountCode}>
              <option value="" disabled>Select BANK account</option>
              {(connection?.bankAccounts ?? []).map((account) => (
                <option key={account.code} value={account.code}>
                  {account.code} — {account.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Square clearing BANK account
            <select onChange={(event) => setSquareClearingAccountCode(event.target.value)} value={squareClearingAccountCode}>
              <option value="">Select BANK account</option>
              {(connection?.bankAccounts ?? []).map((account) => (
                <option key={account.code} value={account.code}>
                  {account.code} — {account.name}
                </option>
              ))}
            </select>
          </label>
          <button disabled={busy} type="submit">Save Xero accounts</button>
        </form>
      ) : null}
      <div className="booking-contact-actions">
        {status === 'setup_required' || status === 'not_connected' || status === 'disconnected' ? (
          <button disabled={busy || status === 'setup_required'} onClick={() => void connectXero()} type="button">
            Connect Xero
          </button>
        ) : (
          <>
            <button disabled={busy} onClick={() => void connectXero()} type="button">Reconnect Xero</button>
            <button disabled={busy} onClick={() => void disconnect()} type="button">Disconnect</button>
          </>
        )}
      </div>
    </article>
  )
}
